import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Markdown, stripMarkdown } from '../components/Markdown';
import {
  carryMilestone,
  closeMilestone,
  createMilestone,
  getCheckins,
  getCheckinsForPeriod,
  getCheckinStatus,
  getCheckinSteps,
  getMilestones,
  updateCheckinSummary,
  type Checkin,
  type CheckinState,
  type KindStatus,
  type Milestone,
  type MilestoneLevel
} from '../api/checkins';
import { periodLabel, shiftPeriod } from '../utils/periods';
import '../styles/checkins.scss';

const KINDS: Array<KindStatus['kind']> = ['daily', 'weekly', 'monthly'];
const LEVEL_OF: Record<KindStatus['kind'], MilestoneLevel> = {
  daily: 'DAY',
  weekly: 'WEEK',
  monthly: 'MONTH'
};

/** Local (Warsaw-ish) day key, matching how the server buckets days. */
function dayKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate()
  ).padStart(2, '0')}`;
}

function daysBack(count: number): Date[] {
  const out: Date[] = [];
  const today = new Date();
  for (let i = count - 1; i >= 0; i -= 1) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    out.push(d);
  }
  return out;
}

/**
 * Timeline of the last ~26 weeks. A gap is information, not a task: the ritual
 * is designed for restart, so nothing here offers to "catch up" on what was missed.
 */
function Timeline({
  checkins,
  selectedDay,
  onSelectDay
}: {
  checkins: Checkin[];
  selectedDay: string | null;
  onSelectDay: (day: string | null) => void;
}) {
  const { t } = useTranslation();
  const byDay = useMemo(() => {
    const map = new Map<string, Checkin[]>();
    checkins.forEach((c) => {
      const at = c.finishedAt ?? c.startedAt ?? c.performedAt;
      if (!at) return;
      const key = dayKey(new Date(at));
      map.set(key, [...(map.get(key) ?? []), c]);
    });
    return map;
  }, [checkins]);

  const days = useMemo(() => daysBack(26 * 7), []);
  const weeks: Date[][] = [];
  for (let i = 0; i < days.length; i += 7) weeks.push(days.slice(i, i + 7));

  return (
    <div className="ci-timeline">
      <div className="ci-timeline-grid">
        {weeks.map((week) => (
          <div className="ci-week" key={dayKey(week[0])}>
            {week.map((day) => {
              const key = dayKey(day);
              const entries = byDay.get(key) ?? [];
              const finished = entries.filter((e) => e.state === 'FINISHED');
              const abandoned = entries.filter((e) => e.state === 'ABANDONED');
              const kinds = new Set(finished.map((e) => e.kind));
              // Done all the same; only the shade says nobody called finish.
              const allAuto = finished.length > 0 && finished.every((e) => e.autoClosed);
              const cls = [
                'ci-cell',
                finished.length ? 'has' : '',
                allAuto ? 'auto' : '',
                kinds.has('weekly') ? 'weekly' : '',
                kinds.has('monthly') ? 'monthly' : '',
                !finished.length && abandoned.length ? 'abandoned' : '',
                entries.length ? 'clickable' : '',
                selectedDay === key ? 'selected' : ''
              ]
                .filter(Boolean)
                .join(' ');
              const label = finished.length
                ? `${key}: ${[...kinds].join(', ')}${allAuto ? ` (${t('checkins.autoClosed')})` : ''}`
                : abandoned.length
                  ? `${key}: ${t('checkins.abandoned')}`
                  : key;
              // Any day that recorded something can be opened to read it.
              return entries.length ? (
                <button
                  key={key}
                  type="button"
                  className={cls}
                  title={label}
                  aria-label={label}
                  onClick={() => onSelectDay(selectedDay === key ? null : key)}
                />
              ) : (
                <span key={key} className={cls} title={label} />
              );
            })}
          </div>
        ))}
      </div>
      <div className="ci-legend">
        <span className="ci-cell has" /> {t('checkins.legendDaily')}
        <span className="ci-cell has weekly" /> {t('checkins.legendWeekly')}
        <span className="ci-cell has monthly" /> {t('checkins.legendMonthly')}
        <span className="ci-cell has auto" /> {t('checkins.legendAutoClosed')}
        <span className="ci-cell abandoned" /> {t('checkins.legendAbandoned')}
      </div>
    </div>
  );
}

/** What a past period amounts to, from all of its sessions. */
function periodState(sessions: Checkin[]): CheckinState {
  if (sessions.some((c) => c.state === 'FINISHED')) return 'FINISHED';
  if (sessions.some((c) => c.state === 'IN_PROGRESS' || c.state === 'STARTED')) return 'IN_PROGRESS';
  if (sessions.some((c) => c.state === 'ABANDONED')) return 'ABANDONED';
  return 'NOT_STARTED';
}

/**
 * One ritual's card. Offset 0 is the current period (driven by status); the
 * arrows page through past periods, showing that period's sessions, trail and
 * milestones at the ritual's own level.
 */
function StateCard({ status, current }: { status: KindStatus; current?: Checkin }) {
  const { t } = useTranslation();
  const [offset, setOffset] = useState(0);
  const kind = status.kind;
  const periodKey = offset === 0 ? status.periodKey : shiftPeriod(kind, status.periodKey, offset);
  const past = offset !== 0;

  const sessions = useQuery({
    queryKey: ['checkins', 'period', kind, periodKey],
    queryFn: () => getCheckinsForPeriod(kind, periodKey),
    enabled: past
  });
  const state = past ? periodState(sessions.data ?? []) : status.state;
  const auto = past
    ? (sessions.data ?? []).some((c) => c.state === 'FINISHED') &&
      (sessions.data ?? []).filter((c) => c.state === 'FINISHED').every((c) => c.autoClosed)
    : status.state === 'FINISHED' && Boolean(current?.autoClosed);

  return (
    <div className={`ci-card ci-${state.toLowerCase()} ${past ? 'ci-card-wide' : ''}`}>
      <div className="ci-card-top">
        <div className="ci-card-kind">{t(`checkins.kind.${kind}`)}</div>
        <div className="ci-period-nav">
          <button
            type="button"
            className="ci-nav-btn"
            onClick={() => setOffset((v) => v - 1)}
            title={t('checkins.periodPrev')}
            aria-label={t('checkins.periodPrev')}
          >
            ‹
          </button>
          <span className="ci-period-key">{periodLabel(kind, periodKey)}</span>
          <button
            type="button"
            className="ci-nav-btn"
            onClick={() => setOffset((v) => Math.min(0, v + 1))}
            disabled={!past}
            title={t('checkins.periodNext')}
            aria-label={t('checkins.periodNext')}
          >
            ›
          </button>
          {past && (
            <button type="button" className="ci-link" onClick={() => setOffset(0)}>
              {t('checkins.backToCurrent')}
            </button>
          )}
        </div>
      </div>
      <div className="ci-card-state">
        {past && state === 'NOT_STARTED' ? t('checkins.notHeld') : t(`checkins.state.${state}`)}
        {auto && (
          <span className="ci-auto-badge" title={t('checkins.autoClosedHint')}>
            {t('checkins.autoClosed')}
          </span>
        )}
      </div>
      {past ? (
        <PeriodDetail
          kind={kind}
          periodKey={periodKey}
          sessions={sessions.data}
          loading={sessions.isLoading}
        />
      ) : (
        <CurrentPeriod status={status} current={current} />
      )}
    </div>
  );
}

function CurrentPeriod({ status, current }: { status: KindStatus; current?: Checkin }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  // Either the running session or the one just finished in this period - both
  // can carry a trail worth reading.
  const trailId = status.openSessionId ?? current?.id ?? null;
  const steps = useQuery({
    queryKey: ['checkin-steps', trailId],
    queryFn: () => getCheckinSteps(trailId as string),
    enabled: open && Boolean(trailId)
  });

  const gap =
    status.daysSince === null
      ? t('checkins.never')
      : status.daysSince === 0
        ? t('checkins.today')
        : t('checkins.daysAgo', { count: status.daysSince });

  return (
    <>
      <div className="ci-card-gap">{gap}</div>
      {current?.summary && (
        <Markdown text={current.summary} className="ci-card-summary" />
      )}
      {trailId && (
        <button className="ci-link" onClick={() => setOpen((v) => !v)}>
          {open ? t('checkins.hideTrail') : t('checkins.showTrail')}
        </button>
      )}
      {open && steps.data && (
        <ol className="ci-steps">
          {steps.data.map((s) => (
            <li key={s.id}>
              <span className="ci-step-kind">{s.kind}</span>
              <Markdown text={s.body} className="ci-step-body" />
            </li>
          ))}
          {!steps.data.length && <li className="ci-muted">{t('checkins.noSteps')}</li>}
        </ol>
      )}
    </>
  );
}

/** A past period: its sessions (editable, with their trail) and its milestones. */
function PeriodDetail({
  kind,
  periodKey,
  sessions,
  loading
}: {
  kind: KindStatus['kind'];
  periodKey: string;
  sessions?: Checkin[];
  loading: boolean;
}) {
  const { t } = useTranslation();
  const level = LEVEL_OF[kind];
  const milestones = useQuery({
    queryKey: ['milestones', 'period', level, periodKey],
    queryFn: () => getMilestones(level, false, periodKey)
  });

  return (
    <div className="ci-period">
      <div className="ci-period-sub">{t('checkins.periodSessions')}</div>
      {loading ? (
        <p className="ci-muted">{t('checkins.loading')}</p>
      ) : sessions && sessions.length ? (
        <div className="ci-history">
          {sessions.map((c) => (
            <HistoryRow key={c.id} checkin={c} />
          ))}
        </div>
      ) : (
        <p className="ci-muted">{t('checkins.nothingInPeriod')}</p>
      )}
      <div className="ci-period-sub">
        {t('checkins.periodMilestones', { level: t(`checkins.level.${level}`) })}
      </div>
      {milestones.data && (
        <MilestoneTree
          milestones={milestones.data}
          emptyText={t('checkins.noPeriodMilestones')}
        />
      )}
    </div>
  );
}

function MilestoneTree({
  milestones,
  emptyText
}: {
  milestones: Milestone[];
  emptyText?: string;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['milestones'] });
  const close = useMutation({
    mutationFn: (v: { id: string; status: 'DONE' | 'DROPPED' }) => closeMilestone(v.id, v.status),
    onSuccess: invalidate
  });
  const carry = useMutation({ mutationFn: carryMilestone, onSuccess: invalidate });

  const byParent = useMemo(() => {
    const map = new Map<string | null, Milestone[]>();
    milestones.forEach((m) => {
      const key = m.parentId ?? null;
      map.set(key, [...(map.get(key) ?? []), m]);
    });
    return map;
  }, [milestones]);

  const roots = useMemo(() => {
    const ids = new Set(milestones.map((m) => m.id));
    // A child whose parent is outside the current view still has to show up.
    return milestones.filter((m) => !m.parentId || !ids.has(m.parentId));
  }, [milestones]);

  const row = (m: Milestone, depth: number) => (
    <div key={m.id}>
      <div className={`ci-ms ci-ms-${m.status.toLowerCase()}`} style={{ paddingLeft: depth * 18 }}>
        <span className="ci-ms-level">{t(`checkins.level.${m.level}`)}</span>
        <span className="ci-ms-title">{m.title}</span>
        <span className="ci-ms-period">{m.periodKey}</span>
        {m.status === 'OPEN' ? (
          <span className="ci-ms-actions">
            <button className="ci-link" onClick={() => close.mutate({ id: m.id, status: 'DONE' })}>
              {t('checkins.done')}
            </button>
            <button className="ci-link" onClick={() => carry.mutate(m.id)}>
              {t('checkins.carry')}
            </button>
            <button
              className="ci-link ci-danger"
              onClick={() => close.mutate({ id: m.id, status: 'DROPPED' })}
            >
              {t('checkins.drop')}
            </button>
          </span>
        ) : (
          <span className="ci-ms-status">{t(`checkins.status.${m.status}`)}</span>
        )}
      </div>
      {(byParent.get(m.id) ?? []).map((child) => row(child, depth + 1))}
    </div>
  );

  if (!milestones.length) {
    return <p className="ci-muted">{emptyText ?? t('checkins.noMilestones')}</p>;
  }
  return <div className="ci-ms-tree">{roots.map((m) => row(m, 0))}</div>;
}

/**
 * One history row. A finished check-in's summary stays editable: the ritual is
 * often written in a hurry, and a note you cannot fix is a note you stop trusting.
 */
function HistoryRow({ checkin }: { checkin: Checkin }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<string | null>(null);

  const steps = useQuery({
    queryKey: ['checkin-steps', checkin.id],
    queryFn: () => getCheckinSteps(checkin.id),
    enabled: open
  });

  const save = useMutation({
    mutationFn: (summary: string) => updateCheckinSummary(checkin.id, summary),
    onSuccess: () => {
      setDraft(null);
      queryClient.invalidateQueries({ queryKey: ['checkins'] });
      queryClient.invalidateQueries({ queryKey: ['checkin-status'] });
    }
  });

  const when = checkin.finishedAt ?? checkin.startedAt ?? checkin.performedAt;
  const text = checkin.summary || checkin.note || '';
  const preview = stripMarkdown(text);

  return (
    <div className={`ci-hist ci-hist-${checkin.state.toLowerCase()}`}>
      <button className="ci-hist-head" onClick={() => setOpen((v) => !v)}>
        <span className="ci-hist-date">{new Date(when).toLocaleDateString()}</span>
        <span className="ci-hist-kind">{t(`checkins.kind.${checkin.kind}`)}</span>
        <span className="ci-hist-summary">
          {preview || <span className="ci-muted">{t('checkins.noSummary')}</span>}
        </span>
        {checkin.state !== 'FINISHED' && (
          <span className="ci-hist-state">{t(`checkins.state.${checkin.state}`)}</span>
        )}
        {checkin.autoClosed && (
          <span className="ci-hist-state ci-hist-auto" title={t('checkins.autoClosedHint')}>
            {t('checkins.autoClosed')}
          </span>
        )}
      </button>
      {open && (
        <div className="ci-hist-body">
          {draft === null ? (
            <>
              {text ? (
                <Markdown text={text} className="ci-hist-full" />
              ) : (
                <p className="ci-muted">{t('checkins.noSummary')}</p>
              )}
              <button className="ci-link" onClick={() => setDraft(text)}>
                {t('checkins.editSummary')}
              </button>
            </>
          ) : (
            <div className="ci-edit">
              <textarea
                className="ci-edit-area"
                value={draft}
                autoFocus
                rows={Math.min(24, Math.max(6, draft.split('\n').length + 2))}
                onChange={(e) => setDraft(e.target.value)}
                placeholder={t('checkins.summaryPlaceholder')}
              />
              <div className="ci-edit-actions">
                <button
                  className="ci-btn"
                  disabled={save.isPending}
                  onClick={() => save.mutate(draft)}
                >
                  {t('checkins.save')}
                </button>
                <button className="ci-link" onClick={() => setDraft(null)}>
                  {t('checkins.cancel')}
                </button>
                {save.isError && <span className="ci-danger">{t('checkins.saveFailed')}</span>}
              </div>
              <p className="ci-hint">{t('checkins.markdownHint')}</p>
            </div>
          )}

          {steps.data && steps.data.length > 0 && (
            <ol className="ci-steps">
              {steps.data.map((s) => (
                <li key={s.id}>
                  <span className="ci-step-kind">{s.kind}</span>
                  <Markdown text={s.body} className="ci-step-body" />
                </li>
              ))}
            </ol>
          )}
          {steps.data && !steps.data.length && (
            <p className="ci-muted">{t('checkins.noSteps')}</p>
          )}
        </div>
      )}
    </div>
  );
}

/** Every recorded check-in with its summary - this is where the notes are read. */
function HistoryList({ checkins, selectedDay }: { checkins: Checkin[]; selectedDay: string | null }) {
  const { t } = useTranslation();
  if (!checkins.length) {
    return (
      <p className="ci-muted">{selectedDay ? t('checkins.noneThatDay') : t('checkins.never')}</p>
    );
  }
  return (
    <div className="ci-history">
      {checkins.map((c) => (
        <HistoryRow key={c.id} checkin={c} />
      ))}
    </div>
  );
}

export function CheckinsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [level, setLevel] = useState<MilestoneLevel>('WEEK');
  const [title, setTitle] = useState('');
  const [selectedDay, setSelectedDay] = useState<string | null>(null);

  const status = useQuery({ queryKey: ['checkin-status'], queryFn: getCheckinStatus });
  const history = useQuery({ queryKey: ['checkins'], queryFn: () => getCheckins(300) });
  // The current period at every level (any status) PLUS whatever is still open
  // elsewhere. Fetching only open ones would hide what was just completed and
  // erase the carry history, which is precisely what carrying is meant to keep.
  const milestones = useQuery({
    queryKey: ['milestones'],
    queryFn: async () => {
      const [month, week, day, open] = await Promise.all([
        getMilestones('MONTH'),
        getMilestones('WEEK'),
        getMilestones('DAY'),
        getMilestones()
      ]);
      const seen = new Map<string, Milestone>();
      [...month, ...week, ...day, ...open].forEach((m) => seen.set(m.id, m));
      return [...seen.values()];
    }
  });

  const create = useMutation({
    mutationFn: () => createMilestone({ level, title: title.trim() }),
    onSuccess: () => {
      setTitle('');
      queryClient.invalidateQueries({ queryKey: ['milestones'] });
    }
  });

  return (
    <section className="ci">
      <header className="ci-head">
        <h2 className="ci-title">{t('checkins.title')}</h2>
      </header>

      <div className="ci-cards">
        {KINDS.map((kind) => {
          const s = status.data?.find((x) => x.kind === kind);
          const current = (history.data ?? []).find(
            (c) => c.kind === kind && c.periodKey === s?.periodKey
          );
          return s ? (
            <StateCard key={kind} status={s} current={current} />
          ) : (
            <div className="ci-card" key={kind} />
          );
        })}
      </div>

      <h3 className="ci-section">{t('checkins.timeline')}</h3>
      <Timeline
        checkins={history.data ?? []}
        selectedDay={selectedDay}
        onSelectDay={setSelectedDay}
      />

      <h3 className="ci-section">{t('checkins.milestones')}</h3>
      <form
        className="ci-add"
        onSubmit={(e) => {
          e.preventDefault();
          if (title.trim()) create.mutate();
        }}
      >
        <select value={level} onChange={(e) => setLevel(e.target.value as MilestoneLevel)}>
          <option value="MONTH">{t('checkins.level.MONTH')}</option>
          <option value="WEEK">{t('checkins.level.WEEK')}</option>
          <option value="DAY">{t('checkins.level.DAY')}</option>
        </select>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={t('checkins.milestonePlaceholder')}
        />
        <button type="submit" disabled={!title.trim() || create.isPending}>
          {t('checkins.add')}
        </button>
      </form>
      <MilestoneTree milestones={milestones.data ?? []} />

      <h3 className="ci-section">{t('checkins.history')}</h3>
      {selectedDay && (
        <div className="ci-filter">
          <span>{t('checkins.filteredTo', { day: selectedDay })}</span>
          <button className="ci-link" onClick={() => setSelectedDay(null)}>
            {t('checkins.showAll')}
          </button>
        </div>
      )}
      <HistoryList
        checkins={(history.data ?? []).filter((c) => {
          if (!selectedDay) return true;
          const at = c.finishedAt ?? c.startedAt ?? c.performedAt;
          return at ? dayKey(new Date(at)) === selectedDay : false;
        })}
        selectedDay={selectedDay}
      />
    </section>
  );
}
